import React, { useState, useEffect, useRef, useCallback } from "react";
import scrollama from "scrollama";
import supabase from "../utils/supabase";
import CustomChart from "./CustomChart";
import _, { fill } from "lodash";
import { PacmanLoader } from "react-spinners";
import { stringToColor } from "../utils/color";

function randomColor() {
  const r = _.random(100, 230);
  const g = _.random(100, 230);
  const b = _.random(100, 230);
  return `rgb(${r}, ${g}, ${b})`;
}
const querySupabaseRange = [
  {
    start: "2025-02-01",
    end: "2025-03-01",
  },
  {
    start: "2025-03-01",
    end: "2025-04-01",
  },
  {
    start: "2025-04-01",
    end: "2025-05-01",
  },
  {
    start: "2025-05-01",
    end: "2025-05-12",
  },
  {
    start: "2025-05-11",
    end: "2025-05-15",
  },
  {
    start: "2025-05-15",
    end: new Date().toISOString().split("T")[0], // Current date
  },
];
// Timeline data with dates, titles, and content
const staticTimelineData = [
  {
    date: "September '24 - January '24",
    title: "🌱 Organic Engagement",
    content: `
      <p>By September 2024, the game was 75% done. It had a solid core engine and structure. We were fleshing out some of the content. By that time, I had one or two illustrations made.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">BoardGameGeek Contest</h3>
      <p>The first time I presented <em>River Rats</em> to a wider audience was in the <strong>Traditional Deck Game Design Contest</strong> on <a href="https://boardgamegeek.com/thread/3360328/wip-river-rats-cooperative-card-game-for-1-4-playe" target="_blank" rel="noopener noreferrer">BoardGameGeek</a>. I participated with a rough version, made a version on playincards.io for quick testing and allowing people to play online (highly recommended).</p>
      <p><em>River Rats</em> didn't win the contest, but I received a lot of feedback and comments and gained some visibility. Some of the feedback we actually incorporated into the final game.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Reddit</h3>
      <p>I also did a few posts on Reddit with the initial artwork. Asking the people from <a href="https://www.reddit.com/r/samuraijack/comments/1fnsb3n/do_you_think_my_games_artwork_is_in_similar_style/" target="_blank" rel="noopener noreferrer">r/samuraijack</a> about how they thought a piece of art would fit in the world of Samurai Jack and asked for input on graphic design for the cards on <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1fy97tq/which_border_and_number_size_do_you_like_better/" target="_blank" rel="noopener noreferrer">r/tabletopgamedesign</a> and r/playingcards. I didn't have any landing page up yet, but it drove initial engagement, and I received valuable input from the communities.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Substack newsletter</h3>
      <p>I also posted one entry on my <a href="https://robinstokkel.substack.com/p/design-diary-3-dream-guardians-in" target="_blank" rel="noopener noreferrer">Substack</a> about <em>River Rats</em>. </p> 
      <li>By that time, I had 925 people on my mailing list and had an <strong>open rate of near 60%</strong>. </li><br>
      <p>Next to these things, Mathijs and I also spoke about it and played the game with friends, and we played the game each month at our <a href="https://www.meetup.com/prototype-testing-meetup/events/308612489/?eventOrigin=your_events" target="_blank" rel="noopener noreferrer">local board game design meetups</a>.</p>
    `,
  },
  {
    date: "February '25",
    title: "🏠 3 months to launch",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">Launch of landing page</h3>
      <p>On the 6th of February, I put live the first version of my <a href="https://web.archive.org/web/20250309174018/http://riverratscardgame.com/" target="_blank" rel="noopener noreferrer">landing page</a>, which then looked something like <a href="https://web.archive.org/web/20250309174018/http://riverratscardgame.com/" target="_blank" rel="noopener noreferrer">this</a>. The landing page allowed me to capture email and was a place to drive people to, either organically or through ads.</p>
      <br><p><strong>For the people who want to set up a landing page:</strong></p>
      <li>If you want to learn how to structure the design and layout of your page, I'd recommend listening to this <a href="https://crowdfundingnerds.com/episode15" target="_blank" rel="noopener noreferrer">podcast episode</a> or read this <a href="https://boardgamedesignlab.com/landing-page-optimization/" target="_blank" rel="noopener noreferrer">article on Board Game Design Lab</a>.</li>
      <li>I took <a href="https://crowdfundingnerds.com/academy/" target="_blank" rel="noopener noreferrer">the courses by Crowdfunding Nerds</a>, since I never ran ads before. The landing page is a key component in the success of capturing emails from people who are interested in your project. The courses went in depth about the landing page, and I felt confident I could make an awesome one myself.</li>
      <li>For building the website, I used Wordpress, with the Astra theme utilising the Elementor builder. I've got it hosted with <a href="https://hostinger.com?REFERRALCODE=JDCRWCSTO9JO" target="_blank" rel="noopener noreferrer">Hostinger</a>. And I used <a href="https://www.mailerlite.com/invite/9b5a6b6d98a76" target="_blank" rel="noopener noreferrer">Mailerlite</a> forms to capture email addresses (they have a <a href="https://wordpress.org/plugins/official-mailerlite-sign-up-forms/" target="_blank" rel="noopener noreferrer">plugin</a>).</li>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Reddit</h3>
      <p>I continued posting on Reddit in February by asking people on <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1ikmrdm/which_box_design_for_my_card_game_do_you_like_best/" target="_blank" rel="noopener noreferrer">r/tabletopgamedesign</a>, <a href="https://www.reddit.com/r/boardgamedesign/comments/1ikms2g/which_box_design_for_my_card_game_do_you_like_best/" target="_blank" rel="noopener noreferrer">r/boardgamedesign</a>, <a href="https://www.reddit.com/r/cardgamedesign/comments/1ikmsb0/which_box_design_for_my_card_game_do_you_like_best/" target="_blank" rel="noopener noreferrer">r/cardgamedesign</a> and <a href="https://www.reddit.com/r/playingcards/comments/1ikmsl4/which_box_design_for_my_card_game_do_you_like_best/" target="_blank" rel="noopener noreferrer">r/playingcards</a> what they thought of the different options for the box. In the end, I went with what I felt was best, but it was interesting to read peoples' arguments for the various options.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Content Creators</h3>
      <p>From the end of February onwards, I started to contact "tastemakers", also known as content creators. I reached out to both YouTubers and Instagram accounts.</p>
      <p>Most YouTubers and reviewers I emailed something like this if it was completely cold:</p>
      <div class="timeline-image-container">
        <img src="./images/jongetsgames.png" alt="Jon Gets Games outreach email" class="timeline-image" />
        <p class="image-caption">Example of outreach email to content creators</p>
      </div>
      <p>I only reached out to channels I really liked and who I thought would actually like <em>River Rats</em>.</p>
      <p>For Instagram-first creators, I just slid in their DMs.</p>
      <p>I had to pay for some of the creators and spent a total of $2,300 (approx. €2,000) on four content creators. Most of them made their review, preview or playthrough for free though.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Ads: capturing emails</h3>
      <p>As I mentioned before, this was my first time running ads on Meta. On February 23rd, I started running ads. They immediately drove traffic and lead generation.</p>
      <p>Note: if you want to learn how to set up Meta ads for this purpose, I'd recommend the courses by <a href="https://crowdfundingnerds.com/episode15" target="_blank" rel="noopener noreferrer">Crowdfunding Nerds</a>. And if you want to learn more, check out this <a href="https://boardgamedesignlab.com/how-to-market-your-kickstarter-campaign/#elementor-toc__heading-anchor-9" target="_blank" rel="noopener noreferrer">Board Game Design Lab article</a>.And if you want to see my ads go <a href="https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=ALL&is_targeted_country=false&media_type=all&search_type=page&view_all_page_id=296537446879456" target="_blank" rel="noopener noreferrer">here</a>.</p>
      <p>The ads were directing people to the landing page. People could fill out their email here, would get a welcome email straight away which would tell them to join the <a href="https://www.facebook.com/groups/523046473768471" target="_blank" rel="noopener noreferrer">River Rats Facebook Group</a>.</p>
      <p>In total, I generated about 2,000 leads, but the Facebook Group only has 61 members now. I don't think the group itself has been very successful. Facebook is definitely also not a medium I like to use, so in the future <strong>I won't use Facebook Groups</strong>.</p>
      <p>The ads definitely drove traffic and lead generation. The cost per lead were still too high, but I was still figuring things out and making tweaks to the ads.</p>
    `,
  },
  {
    date: "March '25",
    title: "📣 2 months to launch",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">Organic Marketing</h3>
      <p>In March, I posted again a few times on <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1j45jhv/the_contentcreatorready_prototypes_are_in/" target="_blank" rel="noopener noreferrer">Reddit about the prototypes arriving</a>, <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1j5ltvh/should_i_shoot_the_trailer_video_with_or_without/" target="_blank" rel="noopener noreferrer">asking for advice whether to include the playmat in my video</a>, <a href="https://www.reddit.com/r/samuraijack/comments/1jkblgx/samurai_jack_inspired_game_with_rich_robotic/" target="_blank" rel="noopener noreferrer">comparing the River Rats artwork to Samurai Jack's</a> and <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1jlz2ot/which_of_these_completely_different_trailers/" target="_blank" rel="noopener noreferrer">asking about a preference for the trailer video version</a>.</p>
      <p>I also sent out two newsletters: <a href="https://preview.mailerlite.io/preview/1323364/emails/148483593347270552" target="_blank" rel="noopener noreferrer">first</a> and <a href="https://preview.mailerlite.io/preview/1323364/emails/149677260839846990" target="_blank" rel="noopener noreferrer">second</a> where I announced the Kickstarter launch date.</p>
      <li>The first was sent to <strong>1,021 subscribers</strong> and had an <strong>open rate of 57.73%</strong>. Subject line: "Prototypes Are Here: You Can Win One! 🐀" and subject line resend to non-openers: "Prototypes Are Ready & Give-Away".</li>
      <li>The second was sent to <strong>1,290 subscribers</strong> and had an <strong>open rate of 61.90%</strong>. Subject line: "Kickstarter launch date & trailer sneak peek" and subject line resend to non-openers: "Watch Teaser Trailer Video & Follow on Kickstarter". </li>
      <br>
      <h3 class="text-lg font-semibold mt-4 mb-2">Content Creators</h3>
      <p>The first content started to go live. First an <a href="https://www.instagram.com/p/DHfwo7EMRYE/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==" target="_blank" rel="noopener noreferrer">Instagram review by @shelf_no_shame</a> who later posted on <a href="https://boardgamegeek.com/thread/3484306/river-rats-short-review" target="_blank" rel="noopener noreferrer">BoardGameGeek</a> too, and then a post by @de_boardgame_familie.</p>
      <p>The Content Creators were never linking the landing page but only the <a href="https://www.kickstarter.com/projects/stokkel/river-rats/" target="_blank" rel="noopener noreferrer">Kickstarter page</a>. As you can see in the chart below, the Reddit posts drove some traffic to the website. You'll also notice a spike in traffic on the Kickstarter page coinciding with my newsletter and the @shel_no_shame Instagram post. I do think the majority of the traffic can be attributed to my newsletter, as other Instagram or BGG posts hardly seem to move the needle.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Kickstarter pre-launch page live</h3>
      <p>On the 19th of March, I launched the pre-launch page. Now I could adjust the flow for the leads so that whenever someone put in their email address, I had the page immediately redirect to the Kickstarter page.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Advertising</h3>
      <p>The cost per lead came closer to €2.50 which was what I was aiming for. The number of leads was increasing steadily.</p>
      <p>The best performing ad was one where <a href="https://www.facebook.com/ads/library/?id=1757895465134670" target="_blank" rel="noopener noreferrer">I had a 16-second video.</a></p>
      <p>As you compare this chart with the chart of the Organic marketing efforts, you'll notice the ads contribute significantly more than the organic efforts. They're just really effective.</p>
    `,
  },
  {
    date: "April '25",
    title: "📝 1 month to launch",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">Organic Marketing</h3>
      <p>I didn't post anything on Reddit, but kept posting on Instagram on a daily basis. Also, there were a few content creators who published their previews, including <a href="https://tabletopping.games/2025/04/02/river-rats/" target="_blank" rel="noopener noreferrer">Tabletopping</a>, <a href="https://www.youtube.com/shorts/154IMOdXsVc" target="_blank" rel="noopener noreferrer">Boardgameaddictuk on YouTube</a>, <a href="https://youtu.be/NRSfide-esg" target="_blank" rel="noopener noreferrer">Nox Spellenzolder</a>, and <a href="https://cardgamer.com/reviews/river-rats-review/" target="_blank" rel="noopener noreferrer">Cardgamer</a>.</p>
      <p>I also sent out <a href="https://preview.mailerlite.io/preview/1323364/emails/151726225242457088" target="_blank" rel="noopener noreferrer">a newsletter updating people about the new reviews</a> and one <a href="https://preview.mailerlite.io/preview/1323364/emails/152849153703019688" target="_blank" rel="noopener noreferrer">where I introduced the rules and told people they could already play the game right now</a>.</p>
      <li>The first was sent to <strong>1,842 subscribers</strong> and had an <strong>open rate of 54.45%</strong>. Subject line: "Two excellent reviews on River Rats 🐀" and subject line to non-openers: "Awesome reviews on River Rats!". </li>
      <li>The second was sent to <strong>2,116 subscribers</strong> and had an <strong>open rate of 39.46%</strong>. Subject line: "You Can Play River Rats Now! 🐀". I did not re-send it to non-openers.</li>
      <br><p>Please remember that traffic from content creators is only driven towards the Kickstarter page. Both my newsletters and the creator's efforts don't seem to have moved the needle much.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Advertising</h3>
      <p>The cost per lead came was pretty stable, so I kept running the same ads. The number of leads was increasing steadily.</p>
      <p>As you compare this chart with the chart of the Organic marketing efforts, you'll notice the ads contribute significantly more than the organic efforts. They're just really effective.</p>
    `,
  },
  {
    date: "May '25 (until 11th)",
    title: "⏱️ Days before the launch",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">Retailers and surveying my community</h3>
      <p>In the week before launch, I contacted a bunch of retailers. I had a list of 400 retailers, and I sent them all a personalised email. I also sent out a <a href="https://docs.google.com/forms/d/e/1FAIpQLSdGLyz53EYguEsAt3_XXS-YznR6u3j3PwuHN5vkuOKZbZPT-w/viewform?usp=sharing&ouid=100016081102617269756" target="_blank" rel="noopener noreferrer">survey to my community</a> to get feedback on the pricing and shipping costs.</p>
      <li>The email was sent to <strong>2,307 subscribers</strong> and had an <strong>open rate of 39.39%</strong>. Subject line: "Preview Kickstarter Page, Leave Feedback & Win". I did not resend to non-openers.</li>
      <br><p>I also did a few interviews with content creators, such as <a href="https://www.youtube.com/watch?v=7DX6VfTmvZA" target="_blank" rel="noopener noreferrer">Specular Board Games</a> and <a href="https://www.youtube.com/watch?v=T-sI0lrVBy4" target="_blank" rel="noopener noreferrer">Games Played Badly</a>.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Content Creators</h3>
      <p>I also had a few more content creators who published their previews, such as <a href="https://www.youtube.com/live/tA2uTCIhbIQ?si=XWAwUycq7VSwY-bv&t=467" target="_blank" rel="noopener noreferrer">Co-Op Guild</a>.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Advertising</h3>
      <p>I kept running the same ads, but I also started running ads to retarget people who had visited the landing page but hadn't signed up for the email list. I spent around €110,- on this, and only received 3 backers. So this was not very successful.</p>
      <p>But what I also did 48 hours before launch, was retargeting people who visited the landing page, but hadn't signed up for the email list. I spent around €110,- on this, and only received 3 backers. So this was not very successful.</p>
      <p>I will have to learn how to do this properly for the next campaign. I might have not had the right messaging or content.</p>
    `,
  },
  {
    date: "May 12th, '25",
    title: "🚀 Day 1 and 2 of the Kickstarter campaign",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">What I did</h3>
      <p>On Launch Day, I did a million things:</p>
      <ul class="list-disc pl-5 mt-2">
        <li>I Whatsapp messaged all my friends and family</li>
        <li>Posted on <a href="https://www.linkedin.com/posts/robinstokkel_crowdfunding-kickstarter-monstersoflochlomond-activity-7327683791966150657-EWhB" target="_blank" rel="noopener noreferrer">LinkedIn</a></li>
        <li>Posted on <a href="https://discord.com/channels/914439166153093170/970599260045344778/1371379298300330115" target="_blank" rel="noopener noreferrer">Discord</a> on several servers</li>
        <li>Published a <a href="https://preview.mailerlite.io/preview/1323364/emails/153387036092400876" target="_blank" rel="noopener noreferrer">newsletter</a> to both my community and the retailers</li>
        <li>Had several creators publish their previews: <a href="https://youtu.be/bfteW9G9L6Q" target="_blank" rel="noopener noreferrer">One Stop Co-Op Shop</a>, <a href="https://www.youtube.com/watch?v=_xXCOwxx_iQ" target="_blank" rel="noopener noreferrer">The Dicetower</a>, <a href="https://www.youtube.com/watch?v=hkfkpNGaqR8" target="_blank" rel="noopener noreferrer">Get Games</a>, <a href="https://www.instagram.com/reel/DJkrhbqSb_L/?utm_source=ig_web_copy_link" target="_blank" rel="noopener noreferrer">TikTokBoardGames</a> on TikTok and IG, <a href="https://www.instagram.com/p/DJmDdu8sN1s/" target="_blank" rel="noopener noreferrer">@boardgameswithjoe</a></li>
        <li>Did a few posts on <a href="https://www.reddit.com/r/tabletopgamedesign/comments/1kksmxp/with_your_help_we_managed_to_turn_a_standard_deck/" target="_blank" rel="noopener noreferrer">Reddit</a> and in specific <a href="https://www.facebook.com/groups/cardgamesdesignguild/posts/2095622417470693/" target="_blank" rel="noopener noreferrer">Facebook Groups</a></li>
      </ul>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">Kickstarter</h3>
      <p>Kickstarter sent out an email to all the project's followers (706 followers by that time). Those followers were mainly obtained through my efforts in Meta Ads in combination with the newsletters I had sent over the past months. Which is why I attribute pretty much all day 1 and 2 gross on Kickstarter to either my own efforts and the Meta Ads.</p>
    `,
  },
  {
    date: "Rest of the campaign",
    title: "🔥 Live campaign period",
    content: `
      <h3 class="text-lg font-semibold mt-4 mb-2">Content Creators</h3>
      <p>More content came out from various creators including <a href="https://unitedcardists.com/viewtopic.php?t=24359" target="_blank" rel="noopener noreferrer">EndersGame on United Cardists</a>, <a href="https://www.youtube.com/watch?v=fBIHqsuYp6c&t=1s" target="_blank" rel="noopener noreferrer">Liege of Games on YouTube</a>, and others.</p>
      
      <h3 class="text-lg font-semibold mt-4 mb-2">My efforts</h3>
      <p>I did:</p>
      <ul class="list-disc pl-5 mt-2">
        <li>A <a href="https://discord.com/channels/823901768282013737/827455883096096768/1374264234300674079" target="_blank" rel="noopener noreferrer">Discord Post</a></li>
        <li>Another <a href="https://www.linkedin.com/posts/robinstokkel_crowdfunding-kickstarter-gamedesign-activity-7330970961426472961-dReW" target="_blank" rel="noopener noreferrer">LinkedIn Post</a></li>
        <li>An interview for a local newspaper: <a href="https://www.leidschdagblad.nl/regio/leiden/trumps-handelspolitiek-brengt-leiderdorps-kaartspel-in-de-knel-in-china-maken-ze-gewoon-de-beste-kaarten/68165554.html" target="_blank" rel="noopener noreferrer">Leidsch Dagblad</a></li>
        <li>A (quickly deleted) post on Reddit r/boardgames (20th of May)</li>
        <li>Newsletters on the <a href="https://preview.mailerlite.io/preview/1323364/emails/154642927516125156" target="_blank" rel="noopener noreferrer">17th</a> and <a href="https://preview.mailerlite.io/preview/1323364/emails/155532017616815355" target="_blank" rel="noopener noreferrer">27th</a> of May and the <a href="https://preview.mailerlite.io/preview/1323364/emails/156273577275950429" target="_blank" rel="noopener noreferrer">4th of June</a></li>
        <li>A <a href="https://www.reddit.com/r/boardgames/comments/1l0lfzw/mod_approved_giveaway_river_rats_card_game/" target="_blank" rel="noopener noreferrer">giveaway on Reddit r/boardgames</a></li>
        <li><a href="https://www.reddit.com/r/playingcards/comments/1l34ry6/mod_approved_giveaway_of_collectors_playing_cards/" target="_blank" rel="noopener noreferrer">r/playingcards - give away</a></li>
        <li>A <a href="https://www.kickstarter.com/projects/stokkel/river-rats/posts/4387337" target="_blank" rel="noopener noreferrer">Kickstarter update</a> where I made the next stretch goal unlockable when the community referred 27 backers - this didn't work at all with only two people participating, so I just unlocked the stretch goal anyway</li>
      </ul>

      <h3 class="text-lg font-semibold mt-4 mb-2">⭐ What worked best</h3>
      <p>Looking at the chart on the left, these are the things that drove direct traffic and revenue:</p>
      <ul class="list-disc pl-5 mt-2">
        <li><strong>May 16th:</strong> EndersGame's efforts on Reddit and BGG. He is well-known and well-regarded in the playing cards and card game communities. So his help really seemed to have contributed, which is also shown in the chart.</li>
        <li><strong>May 20th:</strong> A post on r/boardgames. It was deleted quickly, but it did drive some traffic and revenue as you can see in the chart.</li>
        <li><strong>May 21st:</strong> My LinkedIn posts: I know several people who backed from those posts. Although you do not see their effects immediately in the charts.</li>
        <li><strong>May 17th, 27th and June 4th:</strong> The newsletters</li>
        <li><strong>May 30th:</strong> The giveaway on r/boardgames</li>
      </ul>
    `,
  },
];

const ScrollamaContainer = () => {
  const [timelineData, setTimelineData] = useState(staticTimelineData);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isStepReady, setIsStepReady] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollamaRef = useRef(null);
  const stepsRef = useRef(null);
  const graphicRef = useRef(null);

  const groupKsGrossData = (data) => {
    const group = _.groupBy(data, "channel");
    const formattedData = Object.entries(group).map(([channel, records]) => ({
      label: channel,
      data: records.map((record) => ({
        x: record.date,
        y: record.gross_amount,
      })),
      borderColor: stringToColor(channel),
      backgroundColor: stringToColor(channel),
      fill: true,
      tension: 0.4,
      yAxisID: "y2",
      // Add stacked property for all channels except KS Total
      stack: channel !== "KS Total" ? "gross-stack" : undefined,
    }));
    return formattedData;
  };

  // Fetch marketing data from Supabase for the timeline steps
  const fetchMarketingData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // Define fallback static data for February 2025
    const staticFirstStepData = [
      { name: "Feb 7", value: 9 },
      { name: "Feb 8", value: 1 },
      { name: "Feb 9", value: 43 },
      { name: "Feb 10", value: 26 },
      { name: "Feb 11", value: 63 },
    ];

    try {
      // Check if Supabase client is properly initialized
      if (!supabase || !supabase.from) {
        console.error("Supabase client is not properly initialized");
        throw new Error("Supabase client is not properly initialized");
      }

      // Query Supabase for marketing visitor data where date < '2025-03-01' and channel = 'LP Total'
      const date = querySupabaseRange[currentStepIndex - 1];
      const { data: visitorData, error: visitorError } = await supabase
        .from("marketing_visitor_data")
        .select("*")
        .gte("date", date.start)
        .lt("date", date.end)
        .not(
          "channel",
          "in",
          '("LP: BGG","LP: reddit","LP: organic instagram","LP: Review","KS: Gross (€)")'
        );

      if (visitorError) {
        console.error("Supabase visitor data query error:", visitorError);
        throw visitorError;
      }

      // Query Supabase for marketing events in February 2025
      const { data: eventData, error: eventError } = await supabase
        .from("marketing_events")
        .select("*")
        .gte("date", date.start)
        .lt("date", date.end)

        .order("date", { ascending: true });

      if (eventError) {
        console.error("Supabase event data query error:", eventError);
        throw eventError;
      }

      console.log("Supabase event data received:", eventData);

      const { data: ksgrossData, error: ksgrossError } =
        currentStepIndex !== 6
          ? await supabase
              .from("marketing_ks_gross")
              .select("*")
              .gte("date", date.start)
              .lt("date", date.end)
              .eq("channel", "KS Total") // 👈 thêm điều kiện lọc theo channel
              .order("date", { ascending: true })
          : await supabase
              .from("marketing_ks_gross")
              .select("*")
              .gte("date", date.start)
              .lt("date", date.end)
              .order("date", { ascending: true });

      console.log("Supabase KS gross data received:", ksgrossData);

      if (ksgrossError) {
        console.error("Supabase KS gross data query error:", ksgrossError);
        throw ksgrossError;
      }

      if (visitorData && visitorData.length > 0) {
        //   // Find the date range from the visitor data
        //   const ksgrossGrouped = _.groupBy(ksgrossData, "date");
        //   const formattedData = Object.entries(ksgrossGrouped).map(
        //     ([date, records]) => ({
        //       x: date,
        //       y: _.sumBy(records, "gross_amount"),
        //     })
        //   );
        //   const ksgrossDataset = {
        //     label: "KS Gross Total",
        //     data: formattedData.sort((a, b) => new Date(a.x) - new Date(b.x)), // đảm bảo đúng thứ tự thời gian
        //     borderColor: "rgba(54, 162, 235, 1)", // xanh dương
        //     backgroundColor: "rgba(54, 162, 235, 0.2)",
        //     fill: true,
        //     tension: 0.4,
        //     yAxisID: "y2",
        //   };

        let groupedKsGrossData = groupKsGrossData(ksgrossData) || [];

        const grouped = _.groupBy(visitorData, "channel");
        const dateGrouped = _.groupBy(visitorData, "date");
        const markerDataset = {
          data: [],
          pointRadius: 8,
          showLine: false,
          fill: false,
        };

        const visitorDates = Object.keys(dateGrouped);

        // Group eventData theo ngày để dễ xử lý
        const eventsByDate = _.groupBy(eventData, "date");

        const max = 0;
        // Create a complete dataset with all days in the range
        const formattedVisitorData = {
          datasets: Object.entries(grouped).map(([channel, data]) => ({
            label: channel,
            data: data
              .map((item) => ({ x: item.date, y: item.visitor_count }))
              .sort(
                (a, b) => new Date(a.x).getTime() - new Date(b.x).getTime()
              ), // sắp xếp theo ngày
            borderColor: stringToColor(channel),
            yAxisID: "y",
          })),
        };

        let maxY = 0;

        formattedVisitorData.datasets.forEach((dataset) => {
          dataset.data.forEach((point) => {
            maxY = Math.max(maxY, point.y);
          });
        });

        visitorDates.forEach((date) => {
          const eventsInDate = eventsByDate[date];
          if (!eventsInDate) return;

          eventsInDate.forEach((event, idx) => {
            markerDataset.data.push({
              x: date,
              y: (maxY / 3) * 2 + idx * 10,
              name: event.event_name,
              type: event.event_medium,
            });
          });
        });

        // if (currentStepIndex >= 5) {
        //   formattedVisitorData.datasets.push(ksgrossDataset);
        // }

        if (currentStepIndex === 5 || currentStepIndex === 6) {
          // Handle the case for step 6
          formattedVisitorData.datasets = [
            ...formattedVisitorData.datasets,
            ...groupedKsGrossData,
          ];
        }

        // Update the second timeline step (3 months to launch) with the fetched data
        const updatedTimelineData = [...staticTimelineData];
        updatedTimelineData[currentStepIndex] = {
          ...updatedTimelineData[currentStepIndex],
          chartData: formattedVisitorData.datasets,
          eventData: markerDataset,
          ksgrossData: groupedKsGrossData,
          chartType: "line", // Ensure we use line chart for the Supabase data
        };

        setTimelineData(updatedTimelineData);
      } else {
        console.log(
          "No visitor data returned from Supabase, using fallback data"
        );
        // If no data returned, use fallback static data
        const updatedTimelineData = [...staticTimelineData];
        updatedTimelineData[currentStepIndex] = {
          ...updatedTimelineData[currentStepIndex],
          chartData: staticFirstStepData,
          eventData: [],
          chartType: "line",
        };

        setTimelineData(updatedTimelineData);
      }
    } catch (err) {
    } finally {
      setIsLoading(false);
    }
  }, [currentStepIndex]);
  useEffect(() => {
    // Add a small delay to ensure Supabase client is initialized
    setTimeout(() => {
      fetchMarketingData();
    }, 500);
  }, [currentStepIndex, fetchMarketingData]);

  // Initialize scrollama
  useEffect(() => {
    // Initialize scrollama
    const scroller = scrollama();
    scrollamaRef.current = scroller;

    // Setup the instance, pass callback functions
    scroller
      .setup({
        step: ".step", // required - class name of trigger steps
        offset: 0.5, // optional - offset from the top of the viewport
        debug: false, // optional - set to true to see trigger indicators
        progress: true, // enable progress tracking
      })
      .onStepEnter(({ index, element }) => {
        // Add a more substantial delay when switching steps to avoid ResizeObserver errors
        setIsStepReady(false);

        // First update the index
        setCurrentStepIndex(index);

        // Then wait for the DOM to update before marking the step as ready
        // This longer delay helps prevent ResizeObserver loop errors
        setTimeout(() => {
          setIsStepReady(true);
        }, 300);

        element.classList.add("is-active");
      })
      .onStepExit(({ element }) => {
        // Remove active class
        element.classList.remove("is-active");
      });

    // Setup resize event
    window.addEventListener("resize", handleResize);

    // Call resize on load
    handleResize();

    // Cleanup
    return () => {
      scroller.disable();
      window.removeEventListener("resize", handleResize);
    };
  }, [timelineData]); // Re-initialize when timelineData changes

  const handleResize = () => {
    if (scrollamaRef.current) {
      scrollamaRef.current.resize();
    }
  };

  return (
    <div className="scrollama-container">
      <div className="sticky-graphic" ref={graphicRef}>
        <div
          className="chart-container"
          style={{ width: "100%", maxWidth: "100%", padding: "0" }}
        >
          {isLoading ? (
            <div className="loader-té">
              <PacmanLoader />
            </div>
          ) : (
            <>
              <div className="chart-title">
                <span className="date-chip">
                  {timelineData[currentStepIndex].date}
                </span>
                <h2>{timelineData[currentStepIndex].title}</h2>
              </div>
              {error && <div className="error-message">{error}</div>}
              {/* Only render content when step is ready to prevent ResizeObserver errors */}
              {currentStepIndex === 0 ? (
                <div className="sticky-image-container">
                  <img
                    src="./images/substack_screenshot.png"
                    alt="Substack Newsletter Screenshot"
                    className="sticky-image"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                    }}
                  />
                </div>
              ) : (
                <div
                  className="chart-wrapper"
                  style={{
                    width: "100%",
                    maxWidth: "1200px",
                    margin: "0 auto",
                    opacity: isStepReady ? 1 : 0,
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {timelineData[currentStepIndex] &&
                    timelineData[currentStepIndex]?.chartData &&
                    timelineData[currentStepIndex]?.eventData && (
                      <CustomChart
                        chartData={timelineData[currentStepIndex].chartData}
                        eventData={timelineData[currentStepIndex].eventData}
                        showY2={currentStepIndex >= 5}
                      />
                    )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="steps-container" ref={stepsRef}>
        {timelineData.map((step, index) => (
          <div
            className={`step ${index === currentStepIndex ? "is-active" : ""}`}
            key={index}
            data-step={index + 1}
          >
            <div className="step-header">
              <span className="date-chip">{step.date}</span>
              <h3>{step.title}</h3>
            </div>
            <div className="step-content">
              {/* Render HTML content from the timeline data */}
              <div dangerouslySetInnerHTML={{ __html: step.content }} />
              {step.chartData && step.chartData.length > 1 && (
                <p className="engagement-summary"></p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScrollamaContainer;
